import React, { useState, useEffect } from "react";
import { Modal, Accordion, Form, Badge } from "react-bootstrap";
import { server_post_data } from "../ServiceConnection/serviceconnection";
import {
  search_gmb_categories,
  update_gmb_location_profile,
  get_gmb_attributes,
  update_gmb_attributes,
  get_gmb_location_details,
} from "../ServiceConnection/serviceconnection";
import { handleError, handleSuccess } from "../CommonJquery/CommonJquery";
import Loader from "../pages/Loader";
import "../components/CSS/gmb-premium.css";

const callGmbApi = async (url, extraParams = {}) => {
  let formdata = new FormData();
  Object.keys(extraParams).forEach((key) =>
    formdata.append(key, extraParams[key]),
  );

  const response = await server_post_data(url, formdata);
  if (!response || typeof response.message !== "string") {
    handleError("Unexpected server response");
    return null;
  }
  const parts = response.message.split("~@~");
  const parsed = JSON.parse(parts[1]);
  if (parsed.error) {
    handleError(parsed.message || "Something went wrong");
    return null;
  }
  return parsed;
};

const WEEKDAYS = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const pad = (n) => String(n).padStart(2, "0");

const timeToHHMM = (t) =>
  t ? `${pad(t.hours || 0)}:${pad(t.minutes || 0)}` : "";

const hhmmToTime = (s) => {
  if (!s) return { hours: 0, minutes: 0 };
  const [h, m] = s.split(":").map(Number);
  return { hours: h || 0, minutes: m || 0 };
};

/* ------------------------------------------------------------------ */
/* Single attribute row — renders differently based on Google's       */
/* valueType (BOOL / ENUM / REPEATED_ENUM / URL)                      */
/* ------------------------------------------------------------------ */
const AttributeField = ({ attr, value, onChange }) => {
  const { valueType, valueMetadata, displayName, repeatable } = attr;

  if (valueType === "BOOL") {
    return (
      <Form.Check
        type="switch"
        id={attr.attributeId}
        label={displayName}
        checked={!!value?.values?.[0]}
        onChange={(e) => onChange([e.target.checked])}
      />
    );
  }

  if (valueType === "URL") {
    return (
      <div className="mb-12">
        <label className="gmb-field-label">{displayName}</label>
        <input
          type="text"
          className="form-control"
          placeholder="https://..."
          value={value?.values?.[0] || ""}
          onChange={(e) => onChange([e.target.value])}
        />
      </div>
    );
  }

  if (valueType === "ENUM" || valueType === "REPEATED_ENUM") {
    const current = value?.values || [];
    return (
      <div className="mb-16">
        <label className="gmb-field-label">{displayName}</label>
        <div className="d-flex flex-wrap gap-8">
          {valueMetadata.map((meta) => {
            const active = current.includes(meta.value);
            return (
              <Badge
                key={meta.value}
                bg=""
                className={`gmb-attr-chip ${active ? "active" : ""}`}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (valueType === "ENUM") {
                    onChange([meta.value]);
                  } else if (repeatable) {
                    onChange(
                      active
                        ? current.filter((v) => v !== meta.value)
                        : [...current, meta.value],
                    );
                  }
                }}
              >
                {meta.displayName}
              </Badge>
            );
          })}
        </div>
      </div>
    );
  }

  return null;
};

const GMBProfileEditor = ({ show, onHide, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /* ---- About / Contact / Location / Hours form state ---- */
  const [form, setForm] = useState({
    title: "",
    primary_category_id: "",
    primary_category_name: "",
    description: "",
    opening_date: "",
    primary_phone: "",
    website_uri: "",
    address_lines: "",
    locality: "",
    admin_area: "",
    postal_code: "",
  });

  const [categorySearch, setCategorySearch] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);

  const [hours, setHours] = useState([]); // [{openDay, openTime, closeTime}]

  /* ---- Attributes ---- */
  const [attributeGroups, setAttributeGroups] = useState({});
  const [attributeEdits, setAttributeEdits] = useState({}); // attributeId -> {values,valueType}

  useEffect(() => {
    if (show) {
      loadExistingProfile();
      loadAttributes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const loadExistingProfile = async () => {
    setLoading(true);
    const res = await callGmbApi(get_gmb_location_details);
    setLoading(false);
    if (res?.location) {
      const loc = res.location;
      setForm((f) => ({
        ...f,
        title: loc.title || "",
        primary_category_id: loc.categories?.primaryCategory?.name || "",
        primary_category_name:
          loc.categories?.primaryCategory?.displayName || "",
        description: loc.profile?.description || "",
        primary_phone: loc.phoneNumbers?.primaryPhone || "",
        website_uri: loc.websiteUri || "",
        address_lines: (loc.storefrontAddress?.addressLines || []).join(", "),
        locality: loc.storefrontAddress?.locality || "",
        admin_area: loc.storefrontAddress?.administrativeArea || "",
        postal_code: loc.storefrontAddress?.postalCode || "",
      }));
      setHours(loc.regularHours?.periods || []);
    }
  };

  const loadAttributes = async () => {
    const res = await callGmbApi(get_gmb_attributes);
    if (res) setAttributeGroups(res.attributeGroups || {});
  };

  const searchCategories = async () => {
    if (!categorySearch.trim()) return;
    const res = await callGmbApi(search_gmb_categories, {
      search_term: categorySearch,
    });
    if (res) setCategoryOptions(res.categories || []);
  };

  /* ---------------------------- hours helpers ---------------------------- */

  const addHourPeriod = () =>
    setHours((h) => [
      ...h,
      {
        openDay: "MONDAY",
        openTime: { hours: 9, minutes: 0 },
        closeTime: { hours: 18, minutes: 0 },
      },
    ]);

  const updateHourPeriod = (idx, patch) =>
    setHours((h) => h.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  const removeHourPeriod = (idx) =>
    setHours((h) => h.filter((_, i) => i !== idx));

  /* ---------------------------- save handlers ---------------------------- */

  const saveProfile = async () => {
    setSaving(true);
    const params = {
      title: form.title,
      description: form.description,
      primary_phone: form.primary_phone,
      website_uri: form.website_uri,
      locality: form.locality,
      admin_area: form.admin_area,
      postal_code: form.postal_code,
      address_lines: JSON.stringify(
        form.address_lines
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      ),
      regular_hours: JSON.stringify(hours),
    };
    if (form.opening_date) params.opening_date = form.opening_date;
    if (form.primary_category_id)
      params.primary_category_id = form.primary_category_id;

    const res = await callGmbApi(update_gmb_location_profile, params);
    setSaving(false);
    if (res) {
      handleSuccess("Profile updated successfully");
      onSaved && onSaved();
    }
  };

  const saveAttributes = async () => {
    const payload = Object.keys(attributeEdits).map((attributeId) => ({
      attributeId,
      valueType: attributeEdits[attributeId].valueType,
      values: attributeEdits[attributeId].values,
    }));
    if (payload.length === 0) {
      handleError("No attribute changes to save");
      return;
    }
    setSaving(true);
    const res = await callGmbApi(update_gmb_attributes, {
      attributes: JSON.stringify(payload),
    });
    setSaving(false);
    if (res) {
      handleSuccess("Attributes updated successfully");
      setAttributeEdits({});
      loadAttributes();
    }
  };

  const onAttributeChange = (attr, newValues) => {
    setAttributeEdits((prev) => ({
      ...prev,
      [attr.attributeId]: { valueType: attr.valueType, values: newValues },
    }));
  };

  const currentValueFor = (attr) =>
    attributeEdits[attr.attributeId] || attr.currentValue;

  /* ---------------------------- render ---------------------------- */

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      scrollable
      className="gmb-modal"
      contentClassName="gmb-modal-content"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title>Edit Business Profile</Modal.Title>
      </Modal.Header>
      <Modal.Body
        className="gmb-premium"
        style={{ borderRadius: 0, padding: 22 }}
      >
        {loading && <Loader />}

        <Accordion defaultActiveKey="about" alwaysOpen>
          {/* ---------------- ABOUT YOUR BUSINESS ---------------- */}
          <Accordion.Item eventKey="about">
            <Accordion.Header>About your business</Accordion.Header>
            <Accordion.Body>
              <label className="gmb-field-label">Business name</label>
              <input
                type="text"
                className="form-control mb-12"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />

              <label className="gmb-field-label">Business category</label>
              <input
                type="text"
                className="form-control mb-4"
                placeholder={form.primary_category_name || "Search category..."}
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchCategories()}
              />
              <button
                className="btn btn-sm btn-outline-primary-600 mb-8"
                onClick={searchCategories}
              >
                Search
              </button>
              {categoryOptions.length > 0 && (
                <div className="gmb-category-results">
                  {categoryOptions.map((c) => (
                    <div
                      key={c.name}
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setForm({
                          ...form,
                          primary_category_id: c.name,
                          primary_category_name: c.displayName,
                        });
                        setCategoryOptions([]);
                        setCategorySearch("");
                      }}
                    >
                      {c.displayName}
                    </div>
                  ))}
                </div>
              )}

              <label className="gmb-field-label">Description</label>
              <textarea
                className="form-control mb-12"
                rows={3}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />

              <label className="gmb-field-label">Opening date</label>
              <input
                type="date"
                className="form-control"
                value={form.opening_date}
                onChange={(e) =>
                  setForm({ ...form, opening_date: e.target.value })
                }
              />
            </Accordion.Body>
          </Accordion.Item>

          {/* ---------------- CONTACT INFORMATION ---------------- */}
          <Accordion.Item eventKey="contact">
            <Accordion.Header>Contact information</Accordion.Header>
            <Accordion.Body>
              <label className="gmb-field-label">Phone number</label>
              <input
                type="text"
                className="form-control mb-12"
                value={form.primary_phone}
                onChange={(e) =>
                  setForm({ ...form, primary_phone: e.target.value })
                }
              />

              <label className="gmb-field-label">Website</label>
              <input
                type="text"
                className="form-control mb-12"
                value={form.website_uri}
                onChange={(e) =>
                  setForm({ ...form, website_uri: e.target.value })
                }
              />

              <p className="text-xs text-secondary-light mb-0">
                Menu / booking / ordering links live under Attributes below
                (they vary by category — e.g. "Menu link" for restaurants,
                "Booking link" for salons).
              </p>
            </Accordion.Body>
          </Accordion.Item>

          {/* ---------------- LOCATION AND AREAS ---------------- */}
          <Accordion.Item eventKey="location">
            <Accordion.Header>Location and areas</Accordion.Header>
            <Accordion.Body>
              <label className="gmb-field-label">
                Address lines (comma separated)
              </label>
              <input
                type="text"
                className="form-control mb-12"
                value={form.address_lines}
                onChange={(e) =>
                  setForm({ ...form, address_lines: e.target.value })
                }
              />
              <div className="row">
                <div className="col-md-4 mb-12">
                  <label className="gmb-field-label">City / Locality</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.locality}
                    onChange={(e) =>
                      setForm({ ...form, locality: e.target.value })
                    }
                  />
                </div>
                <div className="col-md-4 mb-12">
                  <label className="gmb-field-label">State</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.admin_area}
                    onChange={(e) =>
                      setForm({ ...form, admin_area: e.target.value })
                    }
                  />
                </div>
                <div className="col-md-4 mb-12">
                  <label className="gmb-field-label">Postal code</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.postal_code}
                    onChange={(e) =>
                      setForm({ ...form, postal_code: e.target.value })
                    }
                  />
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* ---------------- OPENING HOURS ---------------- */}
          <Accordion.Item eventKey="hours">
            <Accordion.Header>Opening hours</Accordion.Header>
            <Accordion.Body>
              {hours.map((p, idx) => (
                <div key={idx} className="gmb-hour-row">
                  <span className="gmb-hour-day">{p.openDay?.slice(0, 3)}</span>
                  <select
                    className="form-control w-auto"
                    value={p.openDay}
                    onChange={(e) =>
                      updateHourPeriod(idx, { openDay: e.target.value })
                    }
                  >
                    {WEEKDAYS.map((d) => (
                      <option key={d} value={d}>
                        {d.slice(0, 3)}
                      </option>
                    ))}
                  </select>
                  <input
                    type="time"
                    className="form-control w-auto"
                    value={timeToHHMM(p.openTime)}
                    onChange={(e) =>
                      updateHourPeriod(idx, {
                        openTime: hhmmToTime(e.target.value),
                      })
                    }
                  />
                  <span className="text-sm">to</span>
                  <input
                    type="time"
                    className="form-control w-auto"
                    value={timeToHHMM(p.closeTime)}
                    onChange={(e) =>
                      updateHourPeriod(idx, {
                        closeTime: hhmmToTime(e.target.value),
                      })
                    }
                  />
                  <button
                    className="btn btn-sm btn-outline-danger-600"
                    onClick={() => removeHourPeriod(idx)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                className="btn btn-sm btn-outline-primary-600 mt-8"
                onClick={addHourPeriod}
              >
                + Add hours
              </button>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        <div className="d-flex justify-content-end mt-16 mb-24">
          <button
            className="btn btn-primary-600"
            disabled={saving}
            onClick={saveProfile}
          >
            {saving ? "Saving..." : "Save profile changes"}
          </button>
        </div>

        <hr />

        {/* ---------------- ATTRIBUTES (dynamic per category) ---------------- */}
        <p className="gmb-section-title mb-12">More</p>
        {Object.keys(attributeGroups).length === 0 && !loading && (
          <p className="text-sm text-secondary-light">
            No attributes available — set a business category first.
          </p>
        )}
        <Accordion alwaysOpen>
          {Object.entries(attributeGroups).map(([groupName, attrs]) => {
            // BOOL attributes are on/off switches — they read much better as a
            // two-up grid than a single tall column. Everything else (chips,
            // URL inputs) stays full-width below them.
            const boolAttrs = attrs.filter((a) => a.valueType === "BOOL");
            const otherAttrs = attrs.filter((a) => a.valueType !== "BOOL");

            return (
              <Accordion.Item eventKey={groupName} key={groupName}>
                <Accordion.Header>{groupName}</Accordion.Header>
                <Accordion.Body>
                  {boolAttrs.length > 0 && (
                    <div
                      className="gmb-attr-grid"
                      style={{
                        marginBottom: otherAttrs.length > 0 ? 18 : 0,
                      }}
                    >
                      {boolAttrs.map((attr) => (
                        <AttributeField
                          key={attr.attributeId}
                          attr={attr}
                          value={currentValueFor(attr)}
                          onChange={(vals) => onAttributeChange(attr, vals)}
                        />
                      ))}
                    </div>
                  )}

                  {otherAttrs.map((attr) => (
                    <AttributeField
                      key={attr.attributeId}
                      attr={attr}
                      value={currentValueFor(attr)}
                      onChange={(vals) => onAttributeChange(attr, vals)}
                    />
                  ))}
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>

        {Object.keys(attributeGroups).length > 0 && (
          <div className="d-flex justify-content-end mt-16">
            <button
              className="btn btn-primary-600"
              disabled={saving}
              onClick={saveAttributes}
            >
              {saving ? "Saving..." : "Save attribute changes"}
            </button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default GMBProfileEditor;